/**
 * AppShell — The three-panel application frame.
 *
 * Composes NavSidebar (left), main content (center),
 * and SidePanelChat (right) into a full-viewport layout.
 *
 * Integrates with UserProfileContext for navigation
 * access control and case/org context display.
 *
 * Usage:
 *   <AppShell navigationItems={sections}>
 *     <YourPageContent />
 *   </AppShell>
 */

import React, { useState, useCallback, useContext, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import UserProfileContext from '../../../context/userprofilecontext';
import { useDashboardCaseStats } from '../../../api/casestats';
import { PROPOSAL_LIST_LIMIT, useLessonProposals } from '../../../api/lessonreview';
import NavSidebar from './NavSidebar';
import TopBar from './TopBar';
import SidePanelChat from './SidePanelChat';
import CenterPanelRenderer from '../../../components/center-panel/CenterPanelRenderer';
import { useCenterPanelSubscription } from '../../../hooks/useCenterPanelSubscription';
import { applyScanCorrections, sendChatMessageV2, getChatSessionContext } from '../../../api/chat';
import { mutate as swrMutate } from 'swr';
import defaultNavigationSections from './navigationConfig';
import { AIAssistantContext } from '../../../context/aiassistantcontext';
import styles from './AppShell.module.css';

const LESSON_REVIEW_PATH = '/lesson_review';
const LESSON_BADGE_REFRESH_MS = 5 * 60 * 1000;

function AppShell({ children, navigationItems }) {
  const [navExpanded, setNavExpanded] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [activeChatSessionId, setActiveChatSessionId] = useState(null);
  // The active chat's session type (SYSTEM_WORK for super-admin chats; undefined
  // for case chats) — the composite fetch must read the same table the panel does.
  const [activeChatSessionType, setActiveChatSessionType] = useState(undefined);
  const handleSessionChange = useCallback((sessionId, sessionType) => {
    setActiveChatSessionId(sessionId);
    setActiveChatSessionType(sessionType);
  }, []);
  const [pendingChatMessage, setPendingChatMessage] = useState(null);
  // A message to SEND (not just pre-fill) on the user's behalf, with metadata — the intake checklist's Save.
  const [pendingChatSend, setPendingChatSend] = useState(null);
  // requestedSessionId is set by descendants (via AIAssistantContext) when they
  // want the chat panel to open AND switch to a specific session — e.g., the
  // dashboard's "Pending HIL Reviews" card. SidePanelChat consumes it once and
  // calls onRequestedSessionConsumed to clear it.
  const [requestedSessionId, setRequestedSessionId] = useState(null);
  // requestedSessionType is the optional session-type hint paired with
  // requestedSessionId. Required for SYSTEM_WORK sessions so the chat-API
  // calls in SidePanelChat route to the global system DDB table instead
  // of per-case-hil. Undefined means "treat as CASE_WORK on the backend"
  // (existing behavior for case-scoped chats).
  const [requestedSessionType, setRequestedSessionType] = useState(undefined);
  const [saveStatus, setSaveStatus] = useState(null); // { type: 'success'|'error', message }

  // Subscribe to center panel render events from the active chat session
  const { panelConfig, modalConfig, clearPanel, clearModal, clearAndSuppressHydration, primePanel, showPanel } = useCenterPanelSubscription(activeChatSessionId);
  // A file chip in the chat reopens that Livi document beside the chat. The panel
  // fetches the record by id; only what the chip knows is passed.
  const openDocument = useCallback((doc) => {
    if (!doc || !doc.documentId) return;
    showPanel({
      layout: 'single',
      components: [{ type: 'DocumentPanel', props: { documentId: doc.documentId, title: doc.title, format: doc.format } }],
    });
  }, [showPanel]);

  const location = useLocation();

  // Clear center panel when switching sessions
  useEffect(() => {
    clearPanel();
  }, [activeChatSessionId, clearPanel]);

  // Composite session-context fetch on session change. Replaces the parallel
  // fan-out of GET /chat/messages + GET /chat/list + GET /chat/center_panel
  // with one request — single ChatHistoryManager init in the lambda, single
  // auth/context Neo4j fetch, single bucket+role STS pair. The downstream
  // SWR hooks (useGetChatHistory, useGetChatList) see their cache primed via
  // swrMutate and return data on first render without firing their own
  // request. The center panel is primed directly via the hook's primePanel.
  // Per-CloudWatch 2026-05-07: parallel fan-out cost ~5-8s per call across
  // separate cold-cache containers; composite consolidates into one.
  useEffect(() => {
    if (!activeChatSessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const ctx = await getChatSessionContext(activeChatSessionId, { sessionType: activeChatSessionType });
        if (cancelled || !ctx) return;
        // Prime SWR caches so the existing hooks (useGetChatHistory,
        // useGetChatList) see data on mount without revalidating. The history
        // key must match SidePanelChat's exactly — including session_type for a
        // SYSTEM_WORK chat, or the prime lands on a key nobody reads.
        const historyPayload = {
          sessionID: ctx.sessionID,
          mode: ctx.is_empty_chat ? 'new_chat' : 'conversation',
          messages: ctx.messages || [],
          message_count: ctx.message_count || 0,
          total_messages: ctx.total_messages || 0,
          is_empty_chat: !!ctx.is_empty_chat,
          state: ctx.state || null,
        };
        swrMutate(['CHAT_HISTORY', {
          sessionID: activeChatSessionId, include_state: 'true',
          ...(activeChatSessionType ? { session_type: activeChatSessionType } : {}),
        }], (cur) => cur ?? historyPayload, false);
        // ^ prime only an EMPTY cache: this response can land after Livi's first committed message (the
        // context call is slow when cold), and overwriting would hide it (review 2026-10-18).
        if (Array.isArray(ctx.chats)) {
          swrMutate('CHAT_LIST', { chats: ctx.chats, count: ctx.chats.length }, false);
        }
        const panelPayload = ctx.centerPanel && ctx.centerPanel.payload;
        if (panelPayload) primePanel(panelPayload);
      } catch (err) {
        // Composite endpoint failed — no problem, downstream hooks still
        // fall back to their own granular fetches. Log for diagnosis.
        console.warn('Composite session-context fetch failed; falling back to granular endpoints', err);
      }
    })();
    return () => { cancelled = true; };
  }, [activeChatSessionId, activeChatSessionType, primePanel]);

  // Clear the agent-driven center panel when the user navigates to a different
  // route via the left nav (or any other router push). Without this, the
  // SchemaAdaptiveEditor or other agent-pushed component keeps rendering even
  // though the underlying route has changed. We also suppress the next
  // hydration cycle: clearPanel alone only clears React state, but the
  // persisted server-side snapshot would otherwise restore the panel on the
  // next chat MESSAGE event, ignoring the user's nav. The suppression lifts
  // automatically on the next live RENDER (agent intentionally drawing on the
  // new view) or session change.
  useEffect(() => {
    clearAndSuppressHydration();
  }, [location.pathname, clearAndSuppressHydration]);

  const { userProfile } = useContext(UserProfileContext);
  // Cached-first: nav gating + the chat badge use last-known stats immediately,
  // then upgrade to live. Avoids the nav sitting half-empty while the slow live
  // /case_stats call resolves against a just-woken case DB.
  const { data: caseStats } = useDashboardCaseStats();

  // BADGE: Livi Lessons (internal) — lesson proposals waiting for review
  // Purpose:  tell a super-admin there are lesson proposals to approve or reject without opening the page.
  // Measure:  pendingLessons.proposals.length — GET lessons/proposals?status=PENDING (livisibly_lesson_review →
  //           lesson_review.list_proposals: proposals on the status index LESSONPROP#PENDING, newest first, at most
  //           PROPOSAL_LIST_LIMIT).
  // Action:   the nav item → /lesson_review, which opens on its "Waiting for review" (PENDING) tab.
  // Consistency: that tab reads the same SWR key ([proposals, 'PENDING']) and approve / reject / edit refresh it, so
  //           the badge and the list are one set. Past the limit both stop at 100, shown as "100+". Super-admins
  //           only (the API refuses anyone else); refreshed on focus and every 5 minutes for proposals that arrive
  //           from case chats.
  const canReviewLessons = !!userProfile?.isSuperAdmin && !!userProfile?.canAccess?.(LESSON_REVIEW_PATH);
  const { data: pendingLessons } = useLessonProposals('PENDING', canReviewLessons,
    { refreshInterval: LESSON_BADGE_REFRESH_MS });
  const pendingLessonCount = canReviewLessons ? (pendingLessons?.proposals?.length || 0) : 0;

  const toggleNav = useCallback(() => {
    setNavExpanded(prev => !prev);
  }, []);

  const toggleChat = useCallback(() => {
    setChatOpen(prev => !prev);
  }, []);

  // Filter navigation items based on user access
  const filteredNavItems = useMemo(() => {
    const sections = navigationItems || defaultNavigationSections;
    if (!userProfile) return [];

    return sections.map(section => ({
      ...section,
      items: section.items.map(item => {
        const hasAccess = userProfile.canAccess(item.path);
        const caseRequired = item.caseRequired || false;
        const orgRequired = item.orgRequired || false;
        const transactionsRequired = item.transactionsRequired || false;
        const customerAdminRequired = item.customerAdminRequired || false;

        let hidden = !hasAccess;

        // A case-scoped item only needs a selected case to be reachable — the
        // destination page is itself gated by CaseDatabaseCheck, so it's safe to
        // show the nav item before stats load. Previously this also required
        // caseStats, which hid the entire case nav until the slow live
        // /case_stats call returned (nav "populated late" after DB wake-up).
        if (caseRequired && !userProfile.currentCase) {
          hidden = true;
        }
        if (orgRequired && !userProfile.currentOrg) {
          hidden = true;
        }
        if (transactionsRequired && !(userProfile.currentCase && caseStats && caseStats.transaction_count > 0)) {
          hidden = true;
        }
        if (customerAdminRequired && !userProfile.isCustomerAdmin?.()) {
          hidden = true;
        }

        let badge = item.badge;
        if (item.path === LESSON_REVIEW_PATH && pendingLessonCount > 0) {
          badge = pendingLessonCount >= PROPOSAL_LIST_LIMIT ? `${PROPOSAL_LIST_LIMIT}+` : pendingLessonCount;
        }

        return { ...item, hidden, badge };
      }),
    })).filter(section =>
      // Keep sections that have at least one visible item
      section.items.some(item => !item.hidden)
    );
  }, [navigationItems, userProfile, caseStats, pendingLessonCount]);

  // Count pending actions for chat badge
  const pendingActions = useMemo(() => {
    if (!caseStats) return 0;
    let count = 0;
    if (caseStats.quality_issue_count > 0) count++;
    if (caseStats.uncategorized_count > 0) count++;
    if (caseStats.flagged_transaction_count > 0) count++;
    return count;
  }, [caseStats]);

  // LIVI is super-admin only during testing (usage billing TBD)
  const canUseAIAssistant = !!userProfile?.isSuperAdmin;

  // Permission to address HIL (pipeline scan review) issues. Today this
  // tracks AI-assistant access (super-admin only); when AI is opened up to
  // more roles we'll likely want a separate group for "pipeline reviewer"
  // — keep the gate in one named constant so that future change is one
  // line rather than hunting through callsites.
  const canResolveHIL = canUseAIAssistant;

  // AIAssistantContext value — exposed to descendants (e.g., the dashboard)
  // so they can request opening the chat panel and switching to a specific
  // session in one call. SidePanelChat consumes the requested session id
  // and calls onRequestedSessionConsumed to clear it.
  const aiAssistantContextValue = useMemo(() => ({
    canUseAIAssistant,
    canResolveHIL,
    isChatOpen: chatOpen,
    // openChatSession(sessionId?, options?) — opens the AI Assistant panel.
    //   sessionId: switch to a specific session
    //   options.pendingMessage: queue a chat message to auto-send once the
    //     session is ready (used by Phase 2 Topic Curation click-through to
    //     post `@view-topic <id> <title>` from the dashboard).
    //   options.sessionType: caller-supplied session-type hint paired with
    //     sessionId. REQUIRED for SYSTEM_WORK sessions so chat-API calls
    //     downstream route to the global system DDB table (super-admin
    //     gated) instead of per-case-hil. Undefined → backend treats as
    //     CASE_WORK (existing behavior for all case-scoped chats).
    openChatSession: (sessionId, options = {}) => {
      if (!canUseAIAssistant) return;
      setChatOpen(true);
      if (sessionId) setRequestedSessionId(sessionId);
      setRequestedSessionType(options?.sessionType);
      if (options && options.pendingMessage) {
        setPendingChatMessage(options.pendingMessage);
      }
    },
    toggleChat,
    // Clear any agent-rendered center-panel override so the current route's
    // default content is restored. Called by NavSidebar on every nav-item
    // click (regardless of whether the route changes) — covers the gap
    // where the user clicks the already-active nav (no router event fires,
    // so the location-pathname effect doesn't run) and expects to "go
    // back" to the route's default. Uses the suppressing variant so the
    // persisted center-panel S3 snapshot doesn't re-hydrate the override
    // on the next AppSync MESSAGE event (matches the route-change effect).
    clearCenterPanelOverride: clearAndSuppressHydration,
  }), [canUseAIAssistant, canResolveHIL, chatOpen, toggleChat, clearAndSuppressHydration]);

  return (
    <AIAssistantContext.Provider value={aiAssistantContextValue}>
    <div className={styles.shell}>
      <NavSidebar
        expanded={navExpanded}
        onToggle={toggleNav}
        items={filteredNavItems}
      />

      <div className={styles.mainArea}>
        <TopBar
          chatOpen={chatOpen}
          onToggleChat={toggleChat}
          pendingActions={pendingActions}
          showChatToggle={canUseAIAssistant}
        />

        <main className={styles.contentArea}>
          {saveStatus && (
            <div className={saveStatus.type === 'error' ? styles.saveStatusError : styles.saveStatusSuccess}>
              {saveStatus.message}
              {saveStatus.type === 'error' && (
                <button className={styles.saveStatusDismiss} onClick={() => setSaveStatus(null)}>✕</button>
              )}
            </div>
          )}
          <CenterPanelRenderer
            panelConfig={panelConfig}
            modalConfig={modalConfig}
            onInteraction={(event) => {
              if (event.type === 'copy_to_chat' && event.message) {
                setPendingChatMessage(event.message);
                if (!chatOpen) setChatOpen(true);
              }
              if (event.type === 'intake_save' && event.message && Array.isArray(event.keep) && event.keep.length) {
                // Narrative intake checklist: send the save as a visible message from the user. The ticked
                // items ride along as metadata, so the backend saves exactly those.
                setPendingChatSend({
                  message: event.message,
                  metadata: { intakeSelection: { intakeId: event.intakeId, keep: event.keep } },
                });
                if (!chatOpen) setChatOpen(true);
              }
              if (event.type === 'agent_undo_requested' && activeChatSessionId) {
                sendChatMessageV2({
                  message: 'Please undo the last bulk change.',
                  sessionID: activeChatSessionId,
                }).catch(() => {});
              }
              if (event.type === 'agent_apply_bulk_edit' && activeChatSessionId) {
                // Reviewer clicked Apply in the preview dialog. Dismiss the dialog
                // and tell the agent to re-call bulk_edit_scan with confirmed=true.
                clearModal();
                sendChatMessageV2({
                  message: 'Approved — please apply the bulk change now (confirmed=true).',
                  sessionID: activeChatSessionId,
                }).catch(() => {});
              }
              if (event.type === 'agent_cancel_bulk_edit' && activeChatSessionId) {
                clearModal();
                sendChatMessageV2({
                  message: 'I canceled the proposed change. Please do not apply it.',
                  sessionID: activeChatSessionId,
                }).catch(() => {});
              }
              if (event.type === 'save_corrections' && activeChatSessionId && event.data) {
                setSaveStatus(null);
                applyScanCorrections({
                  sessionID: activeChatSessionId,
                  data: event.data,
                  artifactType: event.artifactType || 'scan_result',
                  draft: event.draft !== false,
                  retriggerPipeline: event.retriggerPipeline === true,
                })
                  .then(result => {
                    if (result.success) {
                      const isDraft = event.draft !== false;
                      const wasRetrigger = result.retriggerInitiated === true;
                      if (wasRetrigger) {
                        setSaveStatus({
                          type: 'success',
                          message: `Saved. Pipeline restarting for ${result.affectedDocuments || '?'} document(s). New HIL sessions may be created if quality checks fail.`,
                        });
                        // Don't auto-dismiss when pipeline is restarting
                      } else {
                        setSaveStatus({ type: 'success', message: isDraft ? 'Draft saved' : 'Corrections saved' });
                        setTimeout(() => setSaveStatus(null), 4000);
                      }
                      // After finalize, notify agent based on which step was finalized
                      if (!isDraft && !wasRetrigger && activeChatSessionId) {
                        let agentMsg = null;
                        if (event.artifactType === 'scan_result') {
                          agentMsg = 'I have finalized all corrections. Please run hard_score to verify and then approve the scan.';
                        } else if (event.artifactType === 'statement_summary') {
                          agentMsg = "I've finalized the statement summary (Step 2). Please proceed to Step 3 — render the scan editor with view='scan_editor' so I can review the transactions.";
                        }
                        if (agentMsg) {
                          sendChatMessageV2({ message: agentMsg, sessionID: activeChatSessionId }).catch(() => {});
                        }
                      }
                    } else {
                      setSaveStatus({ type: 'error', message: result.error || 'Save failed' });
                    }
                  })
                  .catch(err => {
                    setSaveStatus({ type: 'error', message: err.message || 'Save failed' });
                  });
              }
            }}
          >
            {children}
          </CenterPanelRenderer>
        </main>
      </div>

      {canUseAIAssistant && (
        <SidePanelChat
          open={chatOpen}
          onClose={toggleChat}
          onOpenDocument={openDocument}
          onSessionChange={handleSessionChange}
          pendingMessage={pendingChatMessage}
          onPendingMessageConsumed={() => setPendingChatMessage(null)}
          pendingSend={pendingChatSend}
          onPendingSendConsumed={() => setPendingChatSend(null)}
          requestedSessionId={requestedSessionId}
          requestedSessionType={requestedSessionType}
          onRequestedSessionConsumed={() => {
            setRequestedSessionId(null);
            setRequestedSessionType(undefined);
          }}
        />
      )}
    </div>
    </AIAssistantContext.Provider>
  );
}

export default AppShell;
